import { useNavigate } from 'react-router-dom'
import { Banner, Button } from '@/components/ui'
import { useT } from '@/i18n/useT'

interface DuplicateWarningProps {
  open: boolean
  supplierName: string
  rechnungsnummer: string
  dateInvoice?: string | null
  duplicateId?: string | null
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Shown as an inline amber strip rather than a modal, the way the design does
 * it: the duplicate is a thing to weigh against the extraction still on
 * screen, not an interruption to dismiss before you can look.
 */
export function DuplicateWarning({
  open,
  supplierName,
  rechnungsnummer,
  dateInvoice,
  duplicateId,
  onConfirm,
  onCancel,
}: DuplicateWarningProps) {
  const navigate = useNavigate()
  const { t } = useT()

  if (!open) return null

  const handleViewExisting = () => {
    navigate(duplicateId ? `/database?invoice=${duplicateId}` : '/database')
    onCancel()
  }

  return (
    <Banner
      tone="warn"
      title={t('Possible duplicate invoice')}
      actions={
        <>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            {t('Cancel')}
          </Button>
          <Button variant="outline" size="sm" onClick={onConfirm}>
            {t('Add anyway')}
          </Button>
        </>
      }
    >
      <span>
        {rechnungsnummer || t('(no number)')} {t('from')}{' '}
        <strong style={{ color: 'var(--text)' }}>{supplierName || t('(no supplier)')}</strong>
        {dateInvoice ? ` (${dateInvoice})` : ''} {t('is already in the database.')}{' '}
      </span>
      <button type="button" className="fx-link-quiet" onClick={handleViewExisting}>
        {t('Show the existing invoice')}
      </button>
    </Banner>
  )
}
